import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useRBAC } from '@/hooks/useRBAC';
import { useClinic } from '@/stores/ClinicContext';
import { usePendencias } from '@/hooks/usePendencias';
import { useMuralOpen } from '@/hooks/useMuralNotes';
import { useIntegrationInbox } from '@/hooks/useIntegrationInbox';
import { usePlatformAdmin } from '@/hooks/usePlatformAdmin';
import { Role } from '@/types/auth';
import {
  Home, Users, Calendar, CalendarClock, CalendarSearch, Mic, Stethoscope,
  FileText, FileBarChart2, Calculator, DollarSign, CreditCard,
  Video, MessageSquare, Settings, Box, Glasses, Briefcase,
  UserCog, ExternalLink, Globe, Inbox, Bot, Eye, FileStack,
  Network, Percent, BarChart3, ShieldCheck, SlidersHorizontal, StickyNote, LockOpen,
  FileQuestion, type LucideIcon,
} from 'lucide-react';
import { SidebarRecordingButton } from './SidebarRecordingButton';
import { SidebarExtractButton } from './SidebarExtractButton';
import { SidebarDictateButton } from './SidebarDictateButton';
import { SidebarCameraButton } from './SidebarCameraButton';
import { WakeStatusChip } from '@/components/voice/WakeStatusChip';
import { useCockpitSanfonaEnabled, useSanfona } from './useSanfona';
import { useVisualGoogle } from '@/utils/visualGoogle';
import { useEvolucaoVisual } from '@/utils/evolucaoVisual';

/** Quem vê a fila "Exames sem paciente" (spec 022) — igual ao firestore.rules. */
const EXAM_INBOX_ROLES = [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA];

interface SidebarProps {
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
  /** Quando true, sidebar mostra só ícones no desktop (md+). No mobile,
      o estado é controlado por `isMobileOpen` e ignora esse flag. */
  isDesktopCollapsed?: boolean;
}

interface MenuItem {
  name: string;
  path: string;
  icon: LucideIcon;
  roles: Role[];
  /** When true, opens in a new tab (regular <a>, not react-router NavLink). */
  external?: boolean;
  /** When set, renders a numeric badge next to the label. Hidden when 0. */
  badge?: number;
  /** Visível só para super-admin da plataforma (ignora roles). */
  platformAdminOnly?: boolean;
}

interface MenuGroup {
  /** Header da seção (uppercase). Quando null, itens renderizam sem header. */
  label: string | null;
  items: MenuItem[];
}

export const Sidebar = ({ isMobileOpen, onCloseMobile, isDesktopCollapsed: collapsedPref = false }: SidebarProps) => {
  // Sanfona (por máquina, desligada por padrão): RECOLHIDA, parar o mouse na
  // coluna de ícones abre o menu completo POR CIMA da tela — o conteúdo não
  // encolhe — e ele recolhe quando o mouse sai. Daqui pra baixo o componente
  // só enxerga `isDesktopCollapsed`, que já desconta a espiada.
  const sanfona = useCockpitSanfonaEnabled();
  const visualGoogle = useVisualGoogle();
  const peek = useSanfona();
  const peeking = sanfona && collapsedPref && peek.open;
  const isDesktopCollapsed = collapsedPref && !peeking;
  // Visual novo (spec 017): recolhida vira "navigation rail" do Google —
  // ícone num pill + nome curto embaixo, coluna de 88px.
  const rail = visualGoogle && isDesktopCollapsed;
  // Nova evolução em Linhas/Abas (spec 026, fase 3): na FICHA o rail fica só
  // com os ícones (64 px; o nome aparece no mouse) — a largura vai pro miolo.
  const { pathname } = useLocation();
  const railSemNome = useEvolucaoVisual() !== 'cartoes' && /^\/pacientes\/[^/]+/.test(pathname);
  const collapsedW = visualGoogle && !railSemNome ? 'md:w-[88px]' : 'md:w-16';
  const { profile, clinicId, role } = useAuth();
  const { hasRole } = useRBAC();
  const { isOwner } = useClinic();
  const isPlatformAdmin = usePlatformAdmin();
  // Caixa de Pendências — só consultamos as fontes que o papel logado pode
  // ver, então o contador no sidebar reflete só o que aquele usuário precisa
  // resolver. Devolve 0 silenciosamente quando o papel não tem acesso a
  // nenhuma fonte (ex.: AGENDADOR_EXTERNO).
  const { total: pendenciasTotal } = usePendencias(clinicId, role);
  // Mural de Recados — o badge conta SÓ a coluna "Anotado" (o que precisa de
  // ação de alguém). Somar "Aguardando" faria o número nunca zerar, e badge
  // que nunca zera o olho para de ver.
  const { badgeCount: muralTotal } = useMuralOpen(clinicId, role);
  // Exames do OCT sem ficha certa (spec 022) — mesmos papéis que as regras
  // deixam ler a fila.
  const canSeeExamInbox = isOwner || hasRole(EXAM_INBOX_ROLES);
  const { items: examInbox } = useIntegrationInbox(clinicId, canSeeExamInbox);

  const ALL_EXCEPT_AGENDADOR = Object.values(Role).filter((r) => r !== Role.AGENDADOR_EXTERNO);

  const menuGroups: MenuGroup[] = [
    // ── Faixa Comum: portas de entrada usadas por todos os papéis. Sem
    // header pra não introduzir ruído visual no topo da sidebar.
    {
      label: null,
      items: [
        // Painel exclusivo do AGENDADOR_EXTERNO. Como ele só vê esse item,
        // a sidebar inteira dele vira uma única linha — sem headers.
        { name: 'Painel', path: '/agendador', icon: Briefcase, roles: [Role.AGENDADOR_EXTERNO] },
        { name: 'Dashboard', path: '/', icon: Home, roles: [Role.ADMIN, Role.MEDICO, Role.FINANCEIRO] },
        { name: 'Pendências', path: '/pendencias', icon: Inbox,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.FINANCEIRO],
          badge: pendenciasTotal },
        { name: 'Mural', path: '/mural', icon: StickyNote,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA],
          badge: muralTotal },
        { name: 'Pacientes', path: '/pacientes', icon: Users,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA] },
        { name: 'Exames sem paciente', path: '/exames-sem-paciente', icon: FileQuestion,
          roles: EXAM_INBOX_ROLES, badge: examInbox.length },
        { name: 'Agenda', path: '/agenda', icon: Calendar,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA] },
        { name: 'Chat', path: '/chat', icon: MessageSquare, roles: ALL_EXCEPT_AGENDADOR },
      ],
    },
    {
      label: 'Atendimento médico',
      items: [
        { name: 'Análise de Áudio', path: '/audio-ai', icon: Mic, roles: [Role.ADMIN, Role.MEDICO] },
        { name: 'Prescrições', path: '/prescricoes', icon: Stethoscope, roles: [Role.ADMIN, Role.MEDICO] },
        // Documentos em lote: mesmo documento pra vários pacientes (dia de
        // cirurgia). Só médico — os documentos saem com a assinatura dele.
        { name: 'Documentos em lote', path: '/documentos-lote', icon: FileStack,
          roles: [Role.ADMIN, Role.MEDICO] },
        { name: 'Telemedicina', path: '/telemedicina', icon: Video, roles: [Role.MEDICO, Role.SECRETARIA] },
        // Projetor de optotipos: abre em nova aba pra jogar no monitor do
        // paciente / tablet sem sair da ficha (rota fullscreen, sem Shell).
        { name: 'Optotipos', path: '/optotipos', icon: Eye,
          roles: [Role.ADMIN, Role.MEDICO], external: true },
      ],
    },
    {
      label: 'Recepção & agenda',
      items: [
        { name: 'Agendamentos por Data', path: '/agendamentos', icon: CalendarSearch,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA] },
        // Médico entra porque cirurgia marcada pela secretária (agenda por
        // liberação) cai aqui esperando a autorização dele.
        { name: 'Aprovações', path: '/agenda/aprovacoes', icon: CalendarClock, roles: [Role.ADMIN, Role.SECRETARIA, Role.MEDICO] },
        // Secretária e recepção abrem só pra ler (spec 018).
        { name: 'Liberar agenda', path: '/agenda/liberar', icon: LockOpen,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA] },
        { name: 'Orçamentos', path: '/orcamentos', icon: Calculator,
          roles: [Role.ADMIN, Role.RECEPCIONISTA, Role.SECRETARIA] },
        // Pedidos: paciente solicita lente na clínica.
        { name: 'Pedidos de Lente', path: '/pedidos-lente', icon: Glasses,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA] },
        // Solicitações: clínica solicita ao fornecedor pra entregar ao paciente.
        // Secretária faz o follow-up logístico — daí o acesso adicionado.
        { name: 'Solicitações de Lente', path: '/pedidos-lente/solicitacoes', icon: Glasses,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA] },
        // Portal do Paciente: abre em nova aba pra não derrubar a sessão do
        // staff (o portal usa auth context separado para pacientes).
        { name: 'Portal do Paciente', path: '/portal', icon: Globe,
          roles: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA],
          external: true },
      ],
    },
    {
      label: 'Administração',
      items: [
        { name: 'Financeiro', path: '/financeiro', icon: DollarSign, roles: [Role.ADMIN, Role.FINANCEIRO] },
        // Hub de relatórios imprimíveis — agenda, pacientes, financeiro,
        // clínico. Aberto a quem tem visão administrativa ou de
        // atendimento (médico precisa ver produtividade própria).
        { name: 'Relatórios', path: '/relatorios', icon: FileBarChart2,
          roles: [Role.ADMIN, Role.MEDICO, Role.FINANCEIRO] },
        { name: 'Guias TISS', path: '/tiss-guias', icon: FileText,
          roles: [Role.ADMIN, Role.FINANCEIRO, Role.SECRETARIA] },
        { name: 'Estoque', path: '/estoque', icon: Box, roles: [Role.ADMIN, Role.FINANCEIRO, Role.SECRETARIA] },
        // Equipe: convidar, trocar cargo, ativar/desativar membros (owner-only;
        // ADMIN também enxerga a lista mas só faz leitura).
        { name: 'Equipe', path: '/equipe', icon: UserCog, roles: [Role.ADMIN] },
        { name: 'Planos', path: '/planos', icon: CreditCard, roles: [Role.ADMIN] },
        { name: 'Configurações', path: '/configuracoes', icon: Settings, roles: ALL_EXCEPT_AGENDADOR },
      ],
    },
    // ── Rede de Clínicas / cartão de benefícios (spec 005). Configuração
    // single-player: cada clínica mantém sua tabela e sua curva de ociosidade.
    {
      label: 'Rede',
      items: [
        { name: 'Tabela do cartão', path: '/rede/tabelas', icon: Network, roles: [Role.ADMIN, Role.FINANCEIRO] },
        { name: 'Ociosidade', path: '/rede/ociosidade', icon: Percent, roles: [Role.ADMIN, Role.FINANCEIRO] },
        { name: 'Ocupação', path: '/rede/ocupacao', icon: BarChart3, roles: [Role.ADMIN, Role.FINANCEIRO] },
      ],
    },
    // ── Plataforma: área de super-admin do SaaS (spec 004). Só aparece para
    // quem está na allowlist de platformAdmin — não é por Role nem por clínica.
    {
      label: 'Plataforma',
      items: [
        { name: 'Laboratório de IA', path: '/admin/ia', icon: Bot, roles: [], platformAdminOnly: true },
        { name: 'Credenciamento', path: '/admin/rede', icon: ShieldCheck, roles: [], platformAdminOnly: true },
        { name: 'Config da Rede', path: '/admin/rede/config', icon: SlidersHorizontal, roles: [], platformAdminOnly: true },
      ],
    },
  ];

  const canSee = (item: MenuItem) =>
    item.platformAdminOnly ? isPlatformAdmin : (isOwner || hasRole(item.roles));

  const aside = (
      <aside className={`
        fixed inset-y-0 left-0 z-50 bg-surface-container-low border-r border-outline-variant/10 flex flex-col shrink-0
        transition-[transform,width,box-shadow] duration-300 ease-in-out md:translate-x-0
        ${sanfona ? 'md:absolute md:overflow-hidden' : 'md:relative'}
        ${peeking ? 'md:shadow-[16px_0_40px_-12px_rgb(0_0_0/0.35)]' : ''}
        w-64
        ${isDesktopCollapsed ? collapsedW : 'md:w-64'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
      <div className={`h-16 flex items-center border-b border-outline-variant/10 shrink-0 ${isDesktopCollapsed ? 'md:px-0 md:justify-center px-6' : 'px-6'}`}>
        <span className={`text-amber font-bold tracking-tight ${isDesktopCollapsed ? 'md:text-base text-xl' : 'text-xl'}`}>
          {isDesktopCollapsed ? <span className="hidden md:inline">DR</span> : null}
          <span className={isDesktopCollapsed ? 'md:hidden' : ''}>DRAtento</span>
        </span>
      </div>
      
      <div className="flex-1 overflow-y-auto py-4 custom-scrollbar">
        <nav className={`flex flex-col ${rail ? 'md:px-1 px-3' : isDesktopCollapsed ? 'md:px-2 px-3' : 'px-3'}`}>
          {menuGroups.map((group, groupIdx) => {
            const visibleItems = group.items.filter(canSee);
            if (visibleItems.length === 0) return null;

            return (
              <div
                key={group.label ?? `group-${groupIdx}`}
                // Espaçamento maior entre seções; o primeiro grupo (Comum)
                // não ganha margem-top extra pra colar no topo da sidebar.
                className={groupIdx === 0 ? 'flex flex-col gap-1' : `flex flex-col gap-1 ${rail ? 'mt-2' : 'mt-5'}`}
              >
                {group.label && (
                  <div className={`px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-on-surface-variant/60 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
                    {group.label}
                  </div>
                )}
                {/* Separador discreto entre grupos no modo colapsado, já
                    que o header textual some — sem isso, a sidebar vira
                    uma coluna única de ícones sem hierarquia visual. */}
                {isDesktopCollapsed && groupIdx > 0 && (
                  <div className="hidden md:block mx-2 -mt-1 mb-1 h-px bg-outline-variant/15" aria-hidden="true" />
                )}
                {visibleItems.map((item) =>
                  item.external ? (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onCloseMobile}
                      title={isDesktopCollapsed ? item.name : undefined}
                      className={`flex items-center gap-3 py-2.5 rounded-lg transition-colors text-on-surface-variant hover:bg-surface-container hover:text-on-surface ${
                        isDesktopCollapsed ? 'md:justify-center md:px-0 px-3' : 'px-3'
                      }`}
                    >
                      <item.icon size={18} className="shrink-0" />
                      <span className={`text-sm flex-1 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>{item.name}</span>
                      <ExternalLink size={12} className={`opacity-60 ${isDesktopCollapsed ? 'md:hidden' : ''}`} />
                    </a>
                  ) : rail ? (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onCloseMobile}
                      title={item.name}
                      className="group relative flex flex-col items-center gap-0.5 py-1 text-on-surface-variant"
                    >
                      {({ isActive }) => (
                        <>
                          <span
                            className={`relative grid h-7 w-12 place-items-center rounded-full transition-colors ${
                              isActive
                                ? 'bg-primary-container text-on-primary-container'
                                : 'group-hover:bg-on-surface/[0.08] group-hover:text-on-surface'
                            }`}
                          >
                            <item.icon size={18} className="shrink-0" />
                            {typeof item.badge === 'number' && item.badge > 0 && (
                              <span className="absolute -top-1 left-7 min-w-[16px] h-4 px-1 rounded-full bg-error text-on-error text-[9.5px] font-bold leading-4 text-center">
                                {item.badge > 99 ? '99+' : item.badge}
                              </span>
                            )}
                          </span>
                          {!railSemNome && (
                            <span
                              className={`max-w-[80px] text-center text-[10.5px] leading-[1.15] line-clamp-2 ${
                                isActive ? 'font-semibold text-on-surface' : 'font-medium'
                              }`}
                            >
                              {item.name}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  ) : (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onCloseMobile}
                      title={isDesktopCollapsed ? item.name : undefined}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 py-2.5 rounded-lg transition-colors
                    ${isDesktopCollapsed ? 'md:justify-center md:px-0 px-3' : 'px-3'}
                    ${
                      isActive
                        ? 'bg-amber/10 text-amber font-medium'
                        : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                    }`
                      }
                    >
                      <item.icon size={18} className="shrink-0" />
                      <span className={`text-sm flex-1 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>{item.name}</span>
                      {typeof item.badge === 'number' && item.badge > 0 && (
                        <>
                          {/* Badge expandido: número à direita do label */}
                          <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold bg-warning/20 text-warning ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                          {/* Badge colapsado: dot indicador sobreposto ao ícone */}
                          {isDesktopCollapsed && (
                            <span
                              className="hidden md:block absolute top-1.5 right-2 w-2 h-2 rounded-full bg-warning ring-2 ring-surface-container-low"
                              aria-hidden="true"
                            />
                          )}
                        </>
                      )}
                    </NavLink>
                  ),
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Atalhos fixos: extrair dados, ditar, gravar áudio e câmera — só
          ficam ativos dentro da ficha de um paciente. Também no visual novo:
          o campo de ditado do topo da ficha (spec 017) saiu em 23/10/2026
          pra faixa do paciente sobrar só pro nome, e os atalhos voltaram pra
          cá. O indicador dos microfones abertos (spec 016) foi pra barra do
          topo em 28/10/2026 (`MicsNoTopo`) e o Ditar foi pro lado dele em
          29/10/2026 (`TopbarDictateButton`) — aqui só no celular. */}
      <div className="border-t border-outline-variant/10 shrink-0 pt-2">
        <SidebarExtractButton onCloseMobile={onCloseMobile} isDesktopCollapsed={isDesktopCollapsed} />
        <div className="md:hidden">
          <SidebarDictateButton onCloseMobile={onCloseMobile} isDesktopCollapsed={isDesktopCollapsed} />
        </div>
        <WakeStatusChip isDesktopCollapsed={isDesktopCollapsed} />
        <SidebarRecordingButton onCloseMobile={onCloseMobile} isDesktopCollapsed={isDesktopCollapsed} />
        <SidebarCameraButton onCloseMobile={onCloseMobile} isDesktopCollapsed={isDesktopCollapsed} />
      </div>

      <div className={`pb-4 shrink-0 ${isDesktopCollapsed ? 'md:px-2 px-4' : 'px-4'}`}>
        <div
          className={`flex items-center gap-3 ${isDesktopCollapsed ? 'md:justify-center' : ''}`}
          title={isDesktopCollapsed ? `${profile?.displayName || 'Usuário'} · ${profile?.role ?? ''}` : undefined}
        >
          <div className="w-8 h-8 rounded-full bg-amber/15 text-amber flex items-center justify-center text-sm font-bold shrink-0">
            {profile?.displayName?.charAt(0) || profile?.email?.charAt(0) || 'U'}
          </div>
          <div className={`flex flex-col min-w-0 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
            <span className="text-sm font-medium text-on-surface truncate">{profile?.displayName || 'Usuário'}</span>
            <span className="text-xs text-on-surface-variant uppercase tracking-wider truncate">{profile?.role}</span>
          </div>
        </div>
      </div>
    </aside>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-background/60 backdrop-blur-sm z-40 md:hidden animate-fade-in"
          onClick={onCloseMobile}
        />
      )}

      {sanfona ? (
        // Caixa de layout com a largura de SEMPRE (16 ou 64): o menu espiado
        // cresce dentro dela por cima do conteúdo, sem empurrar nada.
        <div
          {...peek.bind}
          onClick={(e) => {
            // Escolheu um destino no menu espiado → a tela troca, mas o menu
            // FICA enquanto o mouse estiver nele; recolhe quando o mouse sai.
            if (peeking && (e.target as Element).closest('a, button')) peek.release();
          }}
          className={`relative shrink-0 w-0 md:transition-[width] md:duration-300 ${collapsedPref ? collapsedW : 'md:w-64'}`}
        >
          {aside}
        </div>
      ) : (
        aside
      )}
    </>
  );
};
